/**
 * Temporal filter for the RGBD depth summary (§23). The robot's 6 sector values are coarse (quantised to a few
 * levels, ~15 cm apart: 27 / 42 / 57 / 98 / 107 / 115 cm…) and flicker between consecutive 1 Hz frames even when
 * nothing moves. Rules:
 * - a reading ≥ farCm (default 95) is "far": the sensor's ~1 m+ levels are unreliable, so they never make an object;
 * - a sector becomes NEAR only when ≥ 3 of the last 5 readings are near and agree within one step (±20 cm; this robot steps by 15–18 cm) of their
 *   median; it stays near while ≥ 2 agree (hysteresis); values the calibration saw in empty space need 4 of 5;
 * - distance = median of the agreeing readings × calibration scale;
 * - confidence = (agreeing / 5) × stability, where stability = 1 − (state flips in the window / 4): a sector that
 *   flickers near↔far is never confident, however many near readings it has; near readings are further scaled
 *   ×0.8 while uncalibrated and ×0.6 when they are values the calibration saw in empty space.
 * Pure (no imports) so it can be tested on recorded sequences.
 */

export const WINDOW = 5;
/** One quantisation step (cm). */
export const STEP_CM = 20;
export const DEFAULT_FAR_CM = 95;
/** Confidence needed for colours-as-object, nearest, tracks and alerts. */
export const MIN_CONF = 0.6;
/** Near readings are trusted less until the robot is calibrated (its flicker values are unknown). */
export const UNCALIBRATED_RELIABILITY = 0.8;
/** …and least when they are values the calibration saw in empty space. */
export const IGNORED_RELIABILITY = 0.6;

export interface SensorCalibration {
  v: 1;
  /** sector order in the robot's array */
  order: 'ltr' | 'rtl';
  /** per sector: raw values seen in empty space (flicker); they need 4 of 5 agreement to count */
  ignore: number[][];
  /** per sector: share of empty-space frames that read "near" (0..1) */
  noise: number[];
  /** readings ≥ this are "far" */
  farCm: number;
  /** meters = raw cm × scale / 100 */
  scale: number;
  at: string;
  /** notes from the wizard (order unclear, …) */
  notes?: string[];
  /** camera people: distance multiplier from the optional "1 m in front" step */
  peopleScale?: number;
  /** camera bearings come out mirrored (left/right swapped) */
  mirrored?: boolean;
  /** the robot was told `cameraMirrored` (then the client must not flip again) */
  mirrorOnRobot?: boolean;
}

export interface FilteredSector {
  near: boolean;
  /** meters when near */
  d: number | null;
  /** 0..1 agreement of the current state */
  conf: number;
  /** ≥ 3 of the window are 0 / missing */
  nd: boolean;
  /** readings disagree a lot (≥ 3 different steps or spread > 30 cm) */
  noisy: boolean;
  raw: number | null;
  /** filtered value in raw cm (median of agreeing), null when not near */
  filteredCm: number | null;
}

const median = (a: number[]) => {
  const s = [...a].sort((x, y) => x - y);
  const m = s.length >> 1;
  return s.length % 2 ? s[m]! : (s[m - 1]! + s[m]!) / 2;
};

const isIgnored = (v: number, ignore: number[] | undefined) => Boolean(ignore?.some((x) => Math.abs(x - v) <= 2));

/** Filter every frame (oldest first). Returns filtered[frame][sector]. */
export function filterDepth(frames: number[][], cal?: SensorCalibration | null): FilteredSector[][] {
  const n = Math.max(0, ...frames.map((f) => f.length));
  const farCm = cal?.farCm ?? DEFAULT_FAR_CM;
  const scale = cal?.scale ?? 1;
  const out: FilteredSector[][] = [];
  const wasNear: boolean[] = Array(n).fill(false);
  for (let k = 0; k < frames.length; k++) {
    const row: FilteredSector[] = [];
    for (let i = 0; i < n; i++) {
      const win = frames.slice(Math.max(0, k - WINDOW + 1), k + 1).map((f) => f[i]);
      const valid = win.filter((v): v is number => typeof v === 'number' && v > 0);
      const nd = win.length - valid.length >= 3;
      const near = valid.filter((v) => v < farCm);
      let agreeing: number[] = [];
      if (near.length) {
        const m = median(near);
        agreeing = near.filter((v) => Math.abs(v - m) <= STEP_CM);
      }
      // flicker values from the empty-space calibration need stronger agreement
      const onlyIgnored = agreeing.length > 0 && agreeing.every((v) => isIgnored(v, cal?.ignore?.[i]));
      const enter = onlyIgnored ? 4 : 3;
      const stay = onlyIgnored ? 3 : 2;
      const isNear = !nd && agreeing.length >= (wasNear[i] ? stay : enter);
      wasNear[i] = isNear;
      const fc = isNear ? median(agreeing) : null;
      // stability: how often the window flips between "this state" and something else (near↔far flicker)
      const inState = win.map((v) => (typeof v !== 'number' || v <= 0 ? false : isNear ? v < farCm && fc !== null && Math.abs(v - fc) <= STEP_CM : v >= farCm));
      let flips = 0;
      for (let j = 1; j < inState.length; j++) if (inState[j] !== inState[j - 1]) flips++;
      const stability = inState.length > 1 ? 1 - flips / (inState.length - 1) : 0;
      const agreeShare = (isNear ? agreeing.length : nd ? 0 : valid.length - near.length + (near.length - agreeing.length)) / WINDOW;
      const steps = new Set(valid.map((v) => Math.round(v / STEP_CM)));
      const spread = valid.length ? Math.max(...valid) - Math.min(...valid) : 0;
      const raw = frames[k]![i];
      row.push({
        near: isNear,
        d: fc !== null ? (fc * scale) / 100 : null,
        conf: Math.round(agreeShare * stability * (isNear ? (onlyIgnored ? IGNORED_RELIABILITY : cal ? 1 : UNCALIBRATED_RELIABILITY) : 1) * 100) / 100,
        nd,
        noisy: steps.size >= 3 || spread > 30,
        raw: typeof raw === 'number' ? raw : null,
        filteredCm: fc,
      });
    }
    out.push(row);
  }
  return out;
}

/** Trend over the filtered distance series of one object (oldest first, null = not near in that frame). */
export function trendOf(series: { t: number; d: number | null }[]): { trend: 'approaching' | 'static' | 'receding'; speed: number } {
  // longest monotonic run that ends at the latest frame, over consecutive non-null filtered values
  const pts: { t: number; d: number }[] = [];
  for (let k = series.length - 1; k >= 0; k--) {
    const p = series[k]!;
    if (p.d === null) break;
    pts.unshift({ t: p.t, d: p.d });
  }
  if (pts.length < 3) return { trend: 'static', speed: 0 };
  const run = (dir: 1 | -1) => {
    let j = pts.length - 1;
    while (j > 0 && dir * (pts[j]!.d - pts[j - 1]!.d) >= 0) j--;
    const r = pts.slice(j);
    const total = dir * (r[r.length - 1]!.d - r[0]!.d);
    const dt = (r[r.length - 1]!.t - r[0]!.t) / 1000 || 1;
    return { len: r.length, total, speed: total / dt };
  };
  const down = run(-1);
  if (down.len >= 3 && down.total >= 0.15) return { trend: 'approaching', speed: down.speed };
  const up = run(1);
  if (up.len >= 3 && up.total >= 0.15) return { trend: 'receding', speed: up.speed };
  return { trend: 'static', speed: 0 };
}

/** Calibration from the wizard's recordings (raw frames per step). */
export function buildCalibration(rec: { empty: number[][]; centre: number[][]; left: number[][]; right: number[][] }, farCm = DEFAULT_FAR_CM): SensorCalibration {
  const n = Math.max(6, ...rec.empty.map((f) => f.length));
  const notes: string[] = [];
  const ignore: number[][] = [];
  const noise: number[] = [];
  for (let i = 0; i < n; i++) {
    const vals = rec.empty.map((f) => f[i]).filter((v): v is number => typeof v === 'number' && v > 0);
    const nearVals = vals.filter((v) => v < farCm);
    ignore.push([...new Set(nearVals.map((v) => Math.round(v)))].sort((a, b) => a - b));
    noise.push(vals.length ? nearVals.length / vals.length : 0);
  }
  // per sector: share of frames reading "near" above what the same sector reads in empty space
  const score = (frames: number[][]) => Array.from({ length: n }, (_, i) => (frames.length ? frames.filter((f) => typeof f[i] === 'number' && f[i]! > 0 && f[i]! < farCm).length / frames.length : 0) - (noise[i] ?? 0));
  const argmax = (a: number[]) => a.reduce((best, v, i) => (v > a[best]! ? i : best), 0);
  const hl = score(rec.left);
  const hr = score(rec.right);
  const li = argmax(hl);
  const ri = argmax(hr);
  let order: 'ltr' | 'rtl' = 'ltr';
  if (hl[li]! < 0.3 || hr[ri]! < 0.3 || li === ri) notes.push('order-unclear');
  else order = li < ri ? 'ltr' : 'rtl';
  // scale: the person/box stood at 0.5 m in front of the middle sectors
  const centreVals = rec.centre.flatMap((f) => [f[2], f[3]].filter((v): v is number => typeof v === 'number' && v > 0 && v < farCm));
  let scale = 1;
  if (centreVals.length >= 3) scale = Math.max(0.5, Math.min(2, 50 / median(centreVals)));
  else notes.push('scale-unclear');
  return { v: 1, order, ignore, noise, farCm, scale: Math.round(scale * 100) / 100, at: new Date().toISOString(), notes };
}
